import { useState } from 'react';
import { useDiscountCodes } from '@/hooks/useStoreSettings';
import type { DiscountCode } from '@/hooks/useStoreSettings';

interface DiscountManagerProps {
  onToast: (msg: string) => void;
}

export default function DiscountManager({ onToast }: DiscountManagerProps) {
  const { codes, addCode, toggleCode, deleteCode } = useDiscountCodes();
  const [newCode, setNewCode] = useState('');
  const [newPercent, setNewPercent] = useState<number>(10);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [error, setError] = useState('');

  const handleAdd = () => {
    const trimmed = newCode.trim().toUpperCase();
    if (!trimmed) { setError('يرجى إدخال كود الخصم'); return; }
    if (trimmed.length < 3) { setError('الكود يجب أن يكون 3 أحرف على الأقل'); return; }
    if (codes.find(c => c.code.toUpperCase() === trimmed)) { setError('هذا الكود موجود مسبقاً'); return; }
    if (newPercent < 1 || newPercent > 99) { setError('النسبة يجب أن تكون بين 1 و 99'); return; }
    setError('');
    addCode(trimmed, newPercent);
    setNewCode('');
    setNewPercent(10);
    onToast(`تم إضافة كود الخصم "${trimmed}" بنسبة ${newPercent}%`);
  };

  const handleToggle = (code: DiscountCode) => {
    toggleCode(code.code);
    onToast(code.active ? `تم تعطيل كود "${code.code}"` : `تم تفعيل كود "${code.code}"`);
  };

  const handleDelete = (code: string) => {
    deleteCode(code);
    setDeleteConfirm(null);
    onToast(`تم حذف كود "${code}"`);
  };

  const activeCodes = codes.filter(c => c.active).length;

  return (
    <div className="flex flex-col gap-5">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'إجمالي الأكواد', value: codes.length, icon: 'ri-coupon-3-line', color: 'text-gray-700', bg: 'bg-gray-50' },
          { label: 'أكواد نشطة', value: activeCodes, icon: 'ri-checkbox-circle-line', color: 'text-green-700', bg: 'bg-green-50' },
          { label: 'أكواد معطلة', value: codes.length - activeCodes, icon: 'ri-close-circle-line', color: 'text-red-600', bg: 'bg-red-50' },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-xl p-3 flex items-center gap-3`}>
            <div className="w-9 h-9 flex items-center justify-center bg-white rounded-lg flex-shrink-0">
              <i className={`${s.icon} ${s.color} text-base`}></i>
            </div>
            <div>
              <p className={`text-xl font-black ${s.color}`}>{s.value}</p>
              <p className="text-xs text-gray-400">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Add new code */}
      <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
        <h3 className="text-sm font-bold text-[#1a1a1a] mb-3 flex items-center gap-2">
          <i className="ri-add-circle-line text-[#1a1a1a]"></i>
          إضافة كود خصم جديد
        </h3>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <input
              type="text"
              value={newCode}
              onChange={e => { setNewCode(e.target.value); setError(''); }}
              placeholder="مثال: SUMMER30"
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors bg-white uppercase"
              style={{ fontFamily: "'Cairo', sans-serif" }}
            />
          </div>
          <div className="flex items-center gap-2">
            <div className="relative w-28">
              <input
                type="number"
                value={newPercent}
                onChange={e => setNewPercent(Number(e.target.value))}
                min={1}
                max={99}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors bg-white text-center"
              />
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">%</span>
            </div>
            <button
              onClick={handleAdd}
              className="bg-[#1a1a1a] text-white rounded-xl px-5 py-2.5 text-sm font-bold hover:bg-[#333] transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-add-line"></i>
              إضافة
            </button>
          </div>
        </div>
        {error && (
          <p className="text-red-500 text-xs mt-2 flex items-center gap-1">
            <i className="ri-error-warning-line"></i>
            {error}
          </p>
        )}
      </div>

      {/* Codes list */}
      {codes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <div className="w-14 h-14 flex items-center justify-center bg-gray-100 rounded-full mb-3">
            <i className="ri-coupon-3-line text-2xl"></i>
          </div>
          <p className="text-sm">لا توجد أكواد خصم بعد</p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {codes.map(code => (
            <div
              key={code.code}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${
                code.active ? 'bg-white border-gray-100' : 'bg-gray-50 border-gray-100 opacity-60'
              }`}
            >
              {/* Code badge */}
              <div className={`flex-shrink-0 px-3 py-1.5 rounded-lg font-black text-sm tracking-wider ${
                code.active ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-gray-100 text-gray-400 border border-gray-200'
              }`}>
                {code.code}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-sm font-bold ${code.active ? 'text-[#1a1a1a]' : 'text-gray-400'}`}>
                    خصم {code.percent}%
                  </span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                    code.active ? 'bg-green-50 text-green-600 border border-green-200' : 'bg-gray-100 text-gray-400'
                  }`}>
                    {code.active ? 'نشط' : 'معطل'}
                  </span>
                </div>
                <p className="text-gray-400 text-[11px] mt-0.5">
                  استُخدم {code.usageCount} مرة · {new Date(code.createdAt).toLocaleDateString('ar-SA')}
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => handleToggle(code)}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                    code.active
                      ? 'hover:bg-amber-50 text-amber-600'
                      : 'hover:bg-green-50 text-green-600'
                  }`}
                  title={code.active ? 'تعطيل' : 'تفعيل'}
                >
                  <i className={`${code.active ? 'ri-toggle-fill' : 'ri-toggle-line'} text-base`}></i>
                </button>
                <button
                  onClick={() => setDeleteConfirm(code.code)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                  title="حذف"
                >
                  <i className="ri-delete-bin-line text-sm"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" dir="rtl">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-xl mb-4 mx-auto">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="font-bold text-[#1a1a1a] text-center mb-2">حذف كود الخصم</h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              هل أنت متأكد من حذف كود <span className="font-black text-amber-600">"{deleteConfirm}"</span>؟
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 border border-gray-200 rounded-lg py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                إلغاء
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 bg-red-500 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                حذف
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
